import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { PAYMENTS_RUNTIME_WALLETS_ENDPOINTS } from "../constants/wallets.constants";
import type {
  AvailableWalletCurrencies,
  LedgerDirection,
  LedgerOperationType,
  PaginatedRuntime,
  MovementIncidentFilter,
  PaymentMovement,
  PayoutRecheckRun,
  ProviderFloatsOverview,
  RuntimeWallet,
  RuntimeWalletRecentTransactions,
  RuntimeWalletSort,
  RuntimeWalletsSummary,
  RuntimeWalletTransaction,
} from "../types/payments-wallets.types";

export interface ListRuntimeWalletsParams {
  /**
   * INTERNAL users only — used by the cross-org reader/selector. Org members are pinned server-side from the
   * JWT and must NEVER send it (handoff §2).
   */
  organizationId?: string;
  currency?: string;
  status?: string;
  skip?: number;
  limit?: number;
  /** Staff list: part of the organization name. */
  search?: string;
  sort?: RuntimeWalletSort;
  /** Only wallets with any available, pending or blocked balance. */
  withBalance?: boolean;
  /** Bounds on `balance_available + balance_blocked`, as decimal strings. The backend requires `currency`. */
  minHeld?: string;
  maxHeld?: string;
  /** Leave out the wallets of Coloca's own root organization. */
  excludeRoot?: boolean;
}

/** Filters of the staff per-currency summary — the same population as the list, without paging or ranges. */
export interface RuntimeWalletsSummaryParams {
  organizationId?: string;
  status?: string;
  search?: string;
  excludeRoot?: boolean;
}

export interface ListWalletTransactionsParams {
  operation_type?: LedgerOperationType;
  /** Only the legs of one payin, payout, swap or top-up (its reversal included). */
  operation_id?: string;
  direction?: LedgerDirection;
  /** ISO 8601 — range over `posted_at`. */
  from?: string;
  to?: string;
  skip?: number;
  limit?: number;
}

/** Filters of the staff movements list. Amounts are decimal strings; dates ISO 8601 over `posted_at`. */
export interface ListPaymentMovementsParams {
  organizationId?: string;
  currency?: string;
  rail?: string;
  direction?: LedgerDirection;
  operationStatus?: string;
  providerCode?: string;
  from?: string;
  to?: string;
  /** Match what moved in the wallet OR what the operation moved (a payout's net). */
  minAmount?: string;
  maxAmount?: string;
  search?: string;
  excludeRoot?: boolean;
  incident?: MovementIncidentFilter;
  skip?: number;
  limit?: number;
}

/** HTTP client for the NEW payments-runtime wallets (ledger-backed). All calls go through HttpApiService. */
export class PaymentsWalletsService {
  static async list(
    params: ListRuntimeWalletsParams = {},
  ): Promise<AxiosResponse<PaginatedRuntime<RuntimeWallet>>> {
    const { organizationId, search, withBalance, minHeld, maxHeld, excludeRoot, ...rest } = params;
    return HttpApiService.get<PaginatedRuntime<RuntimeWallet>>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.WALLETS, {
      params: {
        ...rest,
        organization_id: organizationId,
        search: search?.trim() || undefined,
        with_balance: withBalance || undefined,
        min_held: minHeld || undefined,
        max_held: maxHeld || undefined,
        exclude_root: excludeRoot || undefined,
      },
    });
  }

  /** Every client wallet's movements, newest first (INTERNAL staff). */
  static async movements(
    params: ListPaymentMovementsParams = {},
  ): Promise<AxiosResponse<PaginatedRuntime<PaymentMovement>>> {
    return HttpApiService.get<PaginatedRuntime<PaymentMovement>>(
      PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.MOVEMENTS,
      {
        params: {
          organization_id: params.organizationId,
          currency: params.currency,
          rail: params.rail,
          direction: params.direction,
          operation_status: params.operationStatus,
          provider_code: params.providerCode,
          from: params.from,
          to: params.to,
          min_amount: params.minAmount || undefined,
          max_amount: params.maxAmount || undefined,
          search: params.search?.trim() || undefined,
          exclude_root: params.excludeRoot || undefined,
          incident: params.incident,
          skip: params.skip,
          limit: params.limit,
        },
      },
    );
  }

  /**
   * Asks the backend to re-read every payout in progress at its provider (INTERNAL staff). One run per
   * two-minute window: asking again returns the run already queued.
   */
  static async requestPayoutRecheck(): Promise<AxiosResponse<PayoutRecheckRun>> {
    return HttpApiService.post<PayoutRecheckRun>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.PAYOUT_RECHECK, {});
  }

  /** The latest payout recheck run. */
  static async payoutRecheckStatus(): Promise<AxiosResponse<PayoutRecheckRun>> {
    return HttpApiService.get<PayoutRecheckRun>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.PAYOUT_RECHECK);
  }

  /** Every treasury float the active routes use (INTERNAL staff only); cached server-side for a minute. */
  static async providerFloats(): Promise<AxiosResponse<ProviderFloatsOverview>> {
    return HttpApiService.get<ProviderFloatsOverview>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.PROVIDER_FLOATS);
  }

  /** Reads every provider again now (INTERNAL staff, `payments:write`). */
  static async refreshProviderFloats(): Promise<AxiosResponse<ProviderFloatsOverview>> {
    return HttpApiService.post<ProviderFloatsOverview>(
      PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.PROVIDER_FLOATS_REFRESH,
      {},
    );
  }

  /** Balances summed per currency across every wallet in scope (INTERNAL staff only). */
  static async summary(
    params: RuntimeWalletsSummaryParams = {},
  ): Promise<AxiosResponse<RuntimeWalletsSummary>> {
    return HttpApiService.get<RuntimeWalletsSummary>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.SUMMARY, {
      params: {
        organization_id: params.organizationId,
        status: params.status,
        search: params.search?.trim() || undefined,
        exclude_root: params.excludeRoot || undefined,
      },
    });
  }

  static async listCurrencies(): Promise<AxiosResponse<AvailableWalletCurrencies>> {
    return HttpApiService.get<AvailableWalletCurrencies>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.CURRENCIES);
  }

  static async getById(id: string): Promise<AxiosResponse<RuntimeWallet>> {
    return HttpApiService.get<RuntimeWallet>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.byId(id));
  }

  /**
   * Activate (create) a wallet. Idempotent: if the org already has one in that currency, the backend returns
   * the existing wallet (also `201`) without modifying it (handoff §3.2). Org members omit
   * `organization_id`.
   */
  static async create(body: {
    currency: string;
    organization_id?: string;
  }): Promise<AxiosResponse<RuntimeWallet>> {
    return HttpApiService.post<RuntimeWallet>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.WALLETS, body);
  }

  /** Ledger statement of a wallet (handoff §3.5). Most recent first; filters are applied server-side. */
  static async listTransactions(
    walletId: string,
    params: ListWalletTransactionsParams = {},
  ): Promise<AxiosResponse<PaginatedRuntime<RuntimeWalletTransaction>>> {
    return HttpApiService.get<PaginatedRuntime<RuntimeWalletTransaction>>(
      PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.transactions(walletId),
      { params },
    );
  }

  /** One statement row by id, wherever it would paginate; 404 when it is not a leg of this wallet. */
  static async getTransaction(
    walletId: string,
    transactionId: string,
  ): Promise<AxiosResponse<RuntimeWalletTransaction>> {
    return HttpApiService.get<RuntimeWalletTransaction>(
      PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.transaction(walletId, transactionId),
    );
  }

  /** The latest `limit` legs of several wallets in one request (home cards). */
  static async listRecentTransactions(
    walletIds: string[],
    limit: number,
  ): Promise<AxiosResponse<{ data: RuntimeWalletRecentTransactions[] }>> {
    return HttpApiService.get<{ data: RuntimeWalletRecentTransactions[] }>(
      PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.RECENT_TRANSACTIONS,
      { params: { wallet_ids: walletIds.join(","), limit } },
    );
  }

  /** Freeze a wallet (INTERNAL staff, `payments:write`). Blocks new commitments; returns the updated wallet. */
  static async freeze(id: string, reason: string): Promise<AxiosResponse<RuntimeWallet>> {
    return HttpApiService.post<RuntimeWallet>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.freeze(id), { reason });
  }

  /** Unfreeze a wallet (INTERNAL staff, `payments:write`). Returns the updated wallet. */
  static async unfreeze(id: string): Promise<AxiosResponse<RuntimeWallet>> {
    return HttpApiService.post<RuntimeWallet>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.unfreeze(id), {});
  }

  /**
   * Saves the order of the organization's wallets, first to last (`payments:write`, 204). Wallets left out go
   * after these, newest first, and the list comes back in this order from then on. Org members omit
   * `organization_id`.
   */
  static async reorder(walletIds: string[], organizationId?: string): Promise<AxiosResponse<void>> {
    return HttpApiService.put<void>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.ORDER, {
      wallet_ids: walletIds,
      organization_id: organizationId,
    });
  }

  /**
   * Deletes a wallet that was never used (`payments:write`, 204). Anything else answers 409 `wallet_in_use`
   * with a message naming why. Activating the currency again brings back the same wallet.
   */
  static async remove(id: string): Promise<AxiosResponse<void>> {
    return HttpApiService.delete<void>(PAYMENTS_RUNTIME_WALLETS_ENDPOINTS.byId(id));
  }
}
